import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Image } from 'react-bootstrap';
import StandardModal from 'components/general/defaultModals/StandardModal';
import { getContestPluginConfig } from '../../services/contestPlugin';
import { getSocket } from '../../socket';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';

const identityIdsFor = (user) => new Set([
  user?.user?._id,
  user?.user?.id,
  user?.user?.tmAuthUserId,
  user?.user?.localUserId,
  user?._id,
  user?.id,
  user?.tmAuthUserId
].map((value) => String(value || '').trim()).filter(Boolean));

export default function ChiefJudgeModal({ meeting, show, onClose }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const url = process.env.REACT_APP_URL;
  const meetingId = meeting?.meetingDetails?.id || meeting?.meetingDetails?._id;
  const isMeetingLocked = Boolean(meeting?.meetingDetails?.isLocked);
  const [chiefContests, setChiefContests] = useState([]);
  const [activeContestIndex, setActiveContestIndex] = useState(0);
  const [usersById, setUsersById] = useState(new Map());
  const [rankingData, setRankingData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [rejectingJudgeId, setRejectingJudgeId] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);

  const identityIds = useMemo(() => identityIdsFor(authUser), [authUser]);
  const activeContest = chiefContests[activeContestIndex] || null;

  useEffect(() => {
    if (!show || !authUser?.token || !meetingId) return;
    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      fetch(`${url}/contesthub/meetings/${meetingId}/setup`, { headers: { Authorization: `Bearer ${authUser.token}` } }).then((response) => response.ok ? response.json() : null),
      fetch(`${url}/users`, { headers: { Authorization: `Bearer ${authUser.token}` } }).then((response) => response.ok ? response.json() : []),
      getContestPluginConfig(authUser.token)
    ]).then(async ([setup, users, config]) => {
      const contestNames = new Map((Array.isArray(config?.contestTypes) ? config.contestTypes : []).map((contest) => [
        String(contest?._id || contest?.id || '').trim(),
        String(contest?.name || '').trim()
      ]));
      const contests = (Array.isArray(setup?.contestJudges) ? setup.contestJudges : [])
        .filter((entry) => identityIds.has(String(entry?.chiefJudge || '').trim()))
        .map((entry) => ({
          contestType: String(entry?.contestType || '').trim(),
          contestName: contestNames.get(String(entry?.contestType || '').trim()) || String(entry?.contestType || '').trim(),
          judges: Array.isArray(entry?.judges) ? entry.judges.map((id) => String(id || '').trim()).filter(Boolean) : [],
          tiebreakingJudge: String(entry?.tiebreakingJudge || '').trim()
        }))
        .filter((entry) => Boolean(entry.contestType));
      const people = new Map((Array.isArray(users) ? users : []).flatMap((person) => {
        const source = person?.user || person;
        const name = `${source?.firstName || ''} ${source?.lastName || ''}`.trim() || source?.email || '';
        const avatarUrl = source?.avatarUrl || source?.avatar || person?.avatarUrl || person?.avatar || '';
        const avatarSrc = resolveAvatarSrc(avatarUrl);
        const initials = getUserInitials(source || person, name || 'Judge');
        const userData = { name: name || 'Judge', avatarSrc, initials };
        const ids = [
          String(person?._id || '').trim(),
          String(person?.id || '').trim(),
          String(person?.tmAuthUserId || '').trim(),
          String(person?.localUserId || '').trim(),
          String(source?._id || '').trim(),
          String(source?.id || '').trim(),
          String(source?.tmAuthUserId || '').trim(),
          String(source?.localUserId || '').trim()
        ].filter(Boolean);
        return ids.map((id) => [id, userData]);
      }));
      const responses = await Promise.all(contests.map(async (entry) => {
        const response = await fetch(`${url}/contesthub/meetings/${meetingId}/rankings?${new URLSearchParams({ contestType: entry.contestType }).toString()}`, {
          headers: { Authorization: `Bearer ${authUser.token}` }
        });
        return [entry.contestType, response.ok ? await response.json() : null];
      }));
      if (cancelled) return;
      setChiefContests(contests);
      setActiveContestIndex((current) => Math.min(current, Math.max(0, contests.length - 1)));
      setUsersById(people);
      setRankingData(Object.fromEntries(responses));
    }).catch(() => {
      if (!cancelled) {
        setChiefContests([]);
        setRankingData({});
      }
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [authUser?.token, identityIds, meetingId, refreshVersion, show, url]);

  useEffect(() => {
    if (!show || !meetingId) return undefined;
    const socket = getSocket();
    const handleRankingUpdated = (payload) => {
      if (String(payload?.meetingId || '') === String(meetingId)) {
        setRefreshVersion((current) => current + 1);
      }
    };
    socket.on('contesthub:ranking-updated', handleRankingUpdated);
    socket.emit('meeting:join', String(meetingId));
    return () => socket.off('contesthub:ranking-updated', handleRankingUpdated);
  }, [meetingId, show]);

  const judgeRankingsMap = useMemo(() => {
    const rankings = rankingData[activeContest?.contestType]?.rankings || [];
    const map = new Map();
    rankings.forEach((entry) => {
      const tmAuthId = String(entry?.judgeTmAuthUserId || '').trim();
      const localId = String(entry?.judgeLocalUserId || '').trim();
      if (tmAuthId) map.set(tmAuthId, entry);
      if (localId) map.set(localId, entry);
    });
    return map;
  }, [rankingData, activeContest]);

  const judgeSubmittedSet = useMemo(() => {
    const map = judgeRankingsMap;
    const set = new Set();
    map.forEach((_val, key) => set.add(key));
    return set;
  }, [judgeRankingsMap]);
  const tiebreakerRanking = rankingData[activeContest?.contestType]?.tiebreakerRanking;
  const tiebreakerSubmitted = Boolean(tiebreakerRanking);
  const tiebreakerRankedSpeakers = useMemo(
    () => Array.isArray(tiebreakerRanking?.rankedSpeakers) ? tiebreakerRanking.rankedSpeakers : [],
    [tiebreakerRanking]
  );
  const getUserData = (id, fallbackName) => {
    const val = usersById.get(id);
    if (val && typeof val === 'object') return val;
    if (typeof val === 'string' && val) return { name: val, avatarSrc: null, initials: getUserInitials(null, val) };
    return { name: fallbackName, avatarSrc: null, initials: getUserInitials(null, fallbackName) };
  };
  const displayName = (id, fallback) => getUserData(id, fallback).name;
  const getUserDataByName = (name) => {
    const normalizedName = String(name || '').trim().toLowerCase();
    let match = null;
    usersById.forEach((userData) => {
      if (!match && String(userData?.name || '').trim().toLowerCase() === normalizedName) match = userData;
    });
    return match || { name, avatarSrc: null, initials: getUserInitials(null, name) };
  };

  const hasTopPlaceTie = useMemo(() => {
    const rankings = rankingData[activeContest?.contestType]?.rankings || [];
    if (!Array.isArray(rankings) || rankings.length === 0) return false;

    const pointsMap = new Map();
    rankings.forEach((entry) => {
      const speakers = Array.isArray(entry?.rankedSpeakers) ? entry.rankedSpeakers : [];
      speakers.forEach((spk) => {
        const key = String(spk?.name || '').trim();
        if (!key) return;

        let points = 0;
        if (spk.place === 1) points = 3;
        else if (spk.place === 2) points = 2;
        else if (spk.place === 3) points = 1;

        if (points > 0) {
          const current = pointsMap.get(key) || { name: key, speechName: spk.speechName || '', points: 0 };
          pointsMap.set(key, {
            name: key,
            speechName: spk.speechName || current.speechName,
            points: current.points + points
          });
        }
      });
    });

    const items = Array.from(pointsMap.values()).sort((a, b) => b.points - a.points);
    if (items.length < 2) return false;

    // Check ties involving winning positions 1, 2, or 3
    if (items[0].points === items[1].points) return true;
    if (items.length >= 3 && items[1].points === items[2].points) return true;
    if (items.length >= 4 && items[2].points === items[3].points) return true;

    return false;
  }, [rankingData, activeContest]);

  const calculatedResults = useMemo(() => {
    const rankings = rankingData[activeContest?.contestType]?.rankings || [];
    if (!Array.isArray(rankings) || rankings.length === 0) return [];

    const pointsMap = new Map();

    rankings.forEach((entry) => {
      const speakers = Array.isArray(entry?.rankedSpeakers) ? entry.rankedSpeakers : [];
      speakers.forEach((spk) => {
        const key = String(spk?.name || '').trim();
        if (!key) return;

        let points = 0;
        if (spk.place === 1) points = 3;
        else if (spk.place === 2) points = 2;
        else if (spk.place === 3) points = 1;

        if (points > 0) {
          const current = pointsMap.get(key) || { name: key, speechName: spk.speechName || '', points: 0 };
          pointsMap.set(key, {
            name: key,
            speechName: spk.speechName || current.speechName,
            points: current.points + points
          });
        }
      });
    });

    const tiebreakerRankMap = new Map();
    tiebreakerRankedSpeakers.forEach((spk, idx) => {
      const nameKey = String(spk?.name || '').trim();
      if (nameKey) {
        tiebreakerRankMap.set(nameKey, spk.place ?? (idx + 1));
      }
    });

    const sorted = Array.from(pointsMap.values()).sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      const rankA = tiebreakerRankMap.has(a.name) ? tiebreakerRankMap.get(a.name) : Infinity;
      const rankB = tiebreakerRankMap.has(b.name) ? tiebreakerRankMap.get(b.name) : Infinity;
      return rankA - rankB;
    });

    return sorted;
  }, [rankingData, activeContest, tiebreakerRankedSpeakers]);

  const contestResults = useMemo(() => {
    const names = [...tiebreakerRankedSpeakers, ...(rankingData[activeContest?.contestType]?.rankings || []).flatMap((entry) => entry?.rankedSpeakers || [])];
    const seen = new Set(calculatedResults.map((result) => result.name.toLowerCase()));
    const participants = [];
    names.forEach((speaker) => {
      const name = String(speaker?.name || '').trim();
      if (!name || seen.has(name.toLowerCase())) return;
      seen.add(name.toLowerCase());
      participants.push({ name, speechName: String(speaker?.speechName || '').trim(), points: 0, isParticipant: true });
    });
    return [...calculatedResults, ...participants];
  }, [activeContest, calculatedResults, rankingData, tiebreakerRankedSpeakers]);

  const rejectSubmission = async (judgeId, isTiebreaker = false) => {
    if (!authUser?.token || !meetingId || !activeContest || !judgeId) return;
    const rejectingKey = `${judgeId}:${isTiebreaker}`;
    setRejectingJudgeId(rejectingKey);
    setActionMessage('');
    try {
      const response = await fetch(`${url}/contesthub/meetings/${meetingId}/rankings`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contestType: activeContest.contestType, judgeId, isTiebreaker })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.Error || 'Unable to reject submission.');

      setRankingData((current) => {
        const contestData = current[activeContest.contestType] || {};

        if (isTiebreaker) {
          return {
            ...current,
            [activeContest.contestType]: {
              ...contestData,
              tiebreakerRanking: null
            }
          };
        }

        // Match both exact judgeId and alternate IDs of the target user
        const targetUserIds = new Set([judgeId]);
        const targetData = getUserData(judgeId, '');
        if (targetData?.name) {
          usersById.forEach((val, id) => {
            const name = typeof val === 'object' ? val.name : val;
            if (name === targetData.name) {
              targetUserIds.add(id);
            }
          });
        }

        return {
          ...current,
          [activeContest.contestType]: {
            ...contestData,
            submissionStatus: (contestData.submissionStatus || []).filter((entry) => (
              !targetUserIds.has(String(entry?.judgeTmAuthUserId || '').trim()) &&
              !targetUserIds.has(String(entry?.judgeLocalUserId || '').trim())
            )),
            rankings: (contestData.rankings || []).filter((entry) => (
              !targetUserIds.has(String(entry?.judgeTmAuthUserId || '').trim()) &&
              !targetUserIds.has(String(entry?.judgeLocalUserId || '').trim())
            ))
          }
        };
      });
      setActionMessage('Submission rejected. The judge may now review and resubmit.');
    } catch (error) {
      setActionMessage(String(error?.message || error));
    } finally {
      setRejectingJudgeId('');
    }
  };

  const body = (
    <div className="chief-judge-modal">
      {isLoading && <div className="chief-judge-empty">Loading submissions...</div>}
      {!isLoading && chiefContests.length === 0 && <div className="chief-judge-empty">No chief judge contests are assigned to you for this meeting.</div>}
      {!isLoading && activeContest && (
        <>
          <h2 className="chief-judge-contest-name">{activeContest.contestName}</h2>
          {chiefContests.length > 1 && (
            <div className="chief-judge-tabs" role="tablist" aria-label="Contest type">
              {chiefContests.map((entry, index) => (
                <button key={entry.contestType} type="button" className={index === activeContestIndex ? 'is-active' : ''} onClick={() => setActiveContestIndex(index)}>
                  {entry.contestName}
                </button>
              ))}
            </div>
          )}
          <section className="chief-judge-section">
            <h3>Judges</h3>
            <ul className="chief-judge-status-list">
              {activeContest.judges.map((judgeId, index) => {
                const isSubmitted = judgeSubmittedSet.has(judgeId);
                const judgeRanking = judgeRankingsMap.get(judgeId);
                const rankedSpeakers = Array.isArray(judgeRanking?.rankedSpeakers) ? judgeRanking.rankedSpeakers : [];
                const userData = getUserData(judgeId, `Judge ${index + 1}`);

                return (
                  <li key={judgeId} className={isSubmitted ? 'is-submitted' : 'is-pending'}>
                    <div className="chief-judge-item-row">
                      <div className="d-flex align-items-center gap-2">
                        {userData.avatarSrc ? (
                          <Image
                            src={userData.avatarSrc}
                            roundedCircle
                            width={32}
                            height={32}
                            alt={userData.name}
                            style={{ objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white bg-primary flex-shrink-0"
                            style={{ width: 32, height: 32, fontSize: '0.75rem' }}
                          >
                            {userData.initials}
                          </div>
                        )}
                        <span>{userData.name}</span>
                      </div>
                      <div className="chief-judge-status-actions">
                        <strong>{isSubmitted ? 'Submitted' : 'Pending'}</strong>
                        {isSubmitted && (
                          <button
                            type="button"
                            onClick={() => rejectSubmission(judgeId, false)}
                            disabled={rejectingJudgeId === `${judgeId}:false`}
                          >
                            {rejectingJudgeId === `${judgeId}:false` ? 'Rejecting...' : 'Reject submission'}
                          </button>
                        )}
                      </div>
                    </div>

                    {isSubmitted && (
                      <div className="chief-judge-submission-card">
                        <div className="chief-judge-submission-header">Submitted Ballot:</div>
                        {rankedSpeakers.length > 0 ? (
                          <div className="chief-judge-submission-places">
                            {rankedSpeakers.map((spk) => (
                              <div key={spk.place || spk.name} className="chief-judge-place-row">
                                <span className={`chief-judge-place-badge place-${spk.place}`}>
                                  {spk.place === 1 ? '1st' : spk.place === 2 ? '2nd' : spk.place === 3 ? '3rd' : `${spk.place}th`} Place:
                                </span>
                                <span className="chief-judge-speaker-name">{spk.name}</span>
                                {spk.speechName && (
                                  <span className="chief-judge-speech-title">"{spk.speechName}"</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="chief-judge-no-places">No ranking details available</div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
              {activeContest.judges.length === 0 && <li className="is-pending"><span>No judges assigned</span></li>}
            </ul>
            {actionMessage && <p className="chief-judge-action-message">{actionMessage}</p>}
          </section>
          {hasTopPlaceTie && (
            <section className="chief-judge-section">
              <h3>Tie Resolution</h3>
              <div className={`chief-judge-status-list ${tiebreakerSubmitted ? 'is-submitted' : 'is-pending'}`}>
                <div className="chief-judge-item-row p-2 border rounded">
                  <div className="d-flex align-items-center gap-2">
                    {(() => {
                      const tiebreakerData = getUserData(activeContest.tiebreakingJudge, 'Tiebreaker Judge');
                      return (
                        <>
                          {tiebreakerData.avatarSrc ? (
                            <Image
                              src={tiebreakerData.avatarSrc}
                              roundedCircle
                              width={32}
                              height={32}
                              alt={tiebreakerData.name}
                              style={{ objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white bg-primary flex-shrink-0"
                              style={{ width: 32, height: 32, fontSize: '0.75rem' }}
                            >
                              {tiebreakerData.initials}
                            </div>
                          )}
                          <span>{activeContest.tiebreakingJudge ? tiebreakerData.name : 'No tiebreaker assigned'}</span>
                        </>
                      );
                    })()}
                  </div>
                  <div className="chief-judge-status-actions">
                    <strong>{tiebreakerSubmitted ? 'Submitted' : 'Pending'}</strong>
                    {tiebreakerSubmitted && activeContest.tiebreakingJudge && (
                      <button
                        type="button"
                        onClick={() => rejectSubmission(activeContest.tiebreakingJudge, true)}
                        disabled={rejectingJudgeId === `${activeContest.tiebreakingJudge}:true`}
                      >
                        {rejectingJudgeId === `${activeContest.tiebreakingJudge}:true` ? 'Rejecting...' : 'Reject submission'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {tiebreakerSubmitted && (
                <div className="chief-judge-submission-card mt-2">
                  <div className="chief-judge-submission-header">
                    Submitted Tiebreaker Ranking ({displayName(activeContest.tiebreakingJudge, 'Tiebreaker')}):
                  </div>
                  {tiebreakerRankedSpeakers.length > 0 ? (
                    <div className="chief-judge-submission-places">
                      {tiebreakerRankedSpeakers.map((spk, idx) => {
                        const place = spk.place || idx + 1;
                        return (
                          <div key={spk.place || spk.name || idx} className="chief-judge-place-row">
                            <span className={`chief-judge-place-badge place-${place}`}>
                              {place === 1 ? '1st' : place === 2 ? '2nd' : place === 3 ? '3rd' : `${place}th`} Place:
                            </span>
                            <span className="chief-judge-speaker-name">{spk.name}</span>
                            {spk.speechName && (
                              <span className="chief-judge-speech-title">"{spk.speechName}"</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="chief-judge-no-places">No tiebreaker ranking details available</div>
                  )}
                </div>
              )}
            </section>
          )}
          <section className="chief-judge-section">
            <h3>Calculated Results</h3>
            {contestResults.length > 0 ? (
              <div className="chief-judge-submission-places">
                {contestResults.map((resItem, idx) => {
                  const place = resItem.isParticipant ? null : idx + 1;
                  const userData = getUserDataByName(resItem.name);
                  return (
                    <div key={resItem.name} className={`chief-judge-place-row chief-judge-result-card${place ? ` chief-judge-result-card--place-${place}` : ' chief-judge-result-card--regular'}`}>
                      {userData.avatarSrc ? <img src={userData.avatarSrc} alt={resItem.name} className="chief-judge-result-avatar" /> : <span className="chief-judge-result-avatar chief-judge-result-avatar--fallback" aria-hidden="true">{userData.initials}</span>}
                      <span className={`chief-judge-place-badge${place ? ` place-${place}` : ''}`}>
                        {place ? `${place === 1 ? '1st' : place === 2 ? '2nd' : place === 3 ? '3rd' : `${place}th`} Place` : 'Participant'}
                      </span>
                      <span className="chief-judge-speaker-name">{resItem.name}</span>
                      {resItem.speechName && (
                        <span className="chief-judge-speech-title">"{resItem.speechName}"</span>
                      )}
                      {!resItem.isParticipant && <span className="ms-auto badge bg-secondary">{resItem.points} {resItem.points === 1 ? 'pt' : 'pts'}</span>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="chief-judge-muted">No submitted results yet to calculate score.</p>
            )}
          </section>
        </>
      )}
    </div>
  );

  return (
    <StandardModal
      show={show}
      handleClose={onClose}
      title="Chief Judge"
      style={{ marginTop: '10px' }}
      bodyClassName="chief-judge-modal-shell"
      hideLogo
      ModalBodyComponent={<fieldset disabled={isMeetingLocked} style={{ border: 'none', padding: 0, margin: 0 }}>{body}</fieldset>}
      ModalFooterComponent={
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    />
  );
}